import React from 'react';
import { TooltipHost } from '@fluentui/react';
import styles from './ThinkingProcessIndicator.module.css';

interface ThinkingProcessData {
  data_source: string;
  reason: string;
  confidence: number;
  expert_guidance?: boolean;
  system_message_type?: string;
  dynamic_system_message_type?: string;
  tools_activated?: string[];
}

interface ThinkingProcessIndicatorProps {
  thinking?: ThinkingProcessData;
  className?: string;
  show?: boolean;
}

export const ThinkingProcessIndicator: React.FC<ThinkingProcessIndicatorProps> = ({ thinking, className, show = true }) => {
  if (!thinking || !show) return null;

  // Generate brief summary of the thinking process
  const generateSummary = (data: ThinkingProcessData): string => {
    const lines: string[] = [];
    
    // Title
    lines.push('🧠 AI Thinking Process');
    lines.push('━━━━━━━━━━━━━━━━━━━━');
    
    // Data source decision
    const sourceMap: Record<string, string> = {
      'general_knowledge': 'AI Knowledge Base',
      'live_search': 'Live Web Search', 
      'documents': 'Document Analysis',
      'office365': 'Office 365 Data',
      'sharepoint': 'SharePoint Documents',
      'realtime_data': 'Real-time Data'
    };
    
    const sourceDisplay = sourceMap[data.data_source] || data.data_source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    lines.push(`📊 Data Source: ${sourceDisplay}`);
    
    // Confidence level with visual indicator
    if (data.confidence > 0) {
      const confidenceLevel = data.confidence >= 0.8 ? 'High' : data.confidence >= 0.5 ? 'Medium' : 'Low';
      const confidenceIcon = data.confidence >= 0.8 ? '🟢' : data.confidence >= 0.5 ? '🟡' : '🔴';
      lines.push(`${confidenceIcon} Confidence: ${confidenceLevel} (${Math.round(data.confidence * 100)}%)`);
    }
    
    // AI persona/system message type
    if (data.dynamic_system_message_type) {
      lines.push(`🤖 AI Persona: ${data.dynamic_system_message_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`);
    } else if (data.system_message_type && data.system_message_type !== 'default') {
      lines.push(`🤖 AI Persona: ${data.system_message_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`);
    }
    
    // Expert guidance
    if (data.expert_guidance) {
      lines.push(`👨‍💼 Expert Guidance: Applied`);
    }
    
    // Tools used
    if (data.tools_activated && data.tools_activated.length > 0) {
      const toolsDisplay = data.tools_activated.map(tool => tool.replace(/_/g, ' ')).join(', ');
      lines.push(`🔧 Tools Used: ${toolsDisplay}`);
    }
    
    // Reasoning (full text, no truncation)
    if (data.reason) {
      lines.push('');
      lines.push('💭 AI Reasoning:');
      // Split long text into multiple lines for better readability
      const words = data.reason.split(' ');
      let currentLine = '   ';
      
      for (const word of words) {
        if (currentLine.length + word.length + 1 > 80) {
          lines.push(currentLine);
          currentLine = '   ' + word;
        } else {
          currentLine += (currentLine === '   ' ? '' : ' ') + word;
        }
      }
      if (currentLine.trim()) {
        lines.push(currentLine);
      }
    }
    
    return lines.join('\n');
  };

  const summary = generateSummary(thinking);

  return (
    <div className={`${styles.thinkingContainer} ${className || ''}`}>
      <TooltipHost content={summary}>
        <div className={styles.thinkingIconWrapper}>
          <span 
            className={styles.thinkingIcon}
            title="AI Thinking Process"
          >
            💭
          </span>
        </div>
      </TooltipHost>
    </div>
  );
};